import idx from 'idx'
import axios from 'axios'
import {API_URL, SAUCE_API_URL} from 'config'
import {push} from 'connected-react-router'

import {normalize} from 'normalizr'
import initialState from 'reducers/initialState'
import {peopleSchema, requestSchema} from 'modules/schema'

import {LOCATION_CHANGE} from 'constants/actions'
import errorDispatcher from 'utils/errorDispatcher'
import {getQueryParams, getPaginationData} from 'utils/api'
import {getAuthorizationHeader} from 'utils/session'
import {paginationFromCount} from "../utils/api";

// Actions
export const VIEW_USER_DETAILS = 'VIEW_USER_DETAILS'
export const API_GET_APPROVED_PEOPLE_REQUEST = 'API_GET_APPROVED_PEOPLE_REQUEST'
export const API_GET_APPROVED_PEOPLE_SUCCESS = 'API_GET_APPROVED_PEOPLE_SUCCESS'
export const API_GET_APPROVED_PEOPLE_ERROR = 'API_GET_APPROVED_PEOPLE_ERROR'
export const API_DELETE_PEOPLE_REQUEST = 'API_DELETE_PEOPLE_REQUEST'
export const API_DELETE_PEOPLE_SUCCESS = 'API_DELETE_PEOPLE_SUCCESS'
export const API_DELETE_PEOPLE_ERROR = 'API_DELETE_PEOPLE_ERROR'
export const API_GET_PEOPLE_REQUESTS_REQUEST = 'API_GET_PEOPLE_REQUESTS_REQUEST'
export const API_GET_PEOPLE_REQUESTS_SUCCESS = 'API_GET_PEOPLE_REQUESTS_SUCCESS'
export const API_GET_PEOPLE_REQUESTS_ERROR = 'API_GET_PEOPLE_REQUESTS_ERROR'
export const API_ACCEPT_REQUESTS_REQUEST = 'API_ACCEPT_REQUESTS_REQUEST'
export const API_ACCEPT_REQUESTS_SUCCESS = 'API_ACCEPT_REQUESTS_SUCCESS'
export const API_ACCEPT_REQUESTS_ERROR = 'API_ACCEPT_REQUESTS_ERROR'
export const API_DENY_REQUESTS_REQUEST = 'API_DENY_REQUESTS_REQUEST'
export const API_DENY_REQUESTS_SUCCESS = 'API_DENY_REQUESTS_SUCCESS'
export const API_DENY_REQUESTS_ERROR = 'API_DENY_REQUESTS_ERROR'
export const API_ASSIGN_GROUPS_REQUEST = 'API_ASSIGN_GROUPS_REQUEST'
export const API_ASSIGN_GROUPS_SUCCESS = 'API_ASSIGN_GROUPS_REQUEST'
export const API_ASSIGN_GROUPS_ERROR = 'API_ASSIGN_GROUPS_ERROR'

// Reducer
export default function reducer(state = initialState.people, action) {
    switch (action.type) {
        case LOCATION_CHANGE:
            return {
                ...state,
                approved: null,
                requests: {
                    ...state.requests,
                    items: null
                }
            }
        case API_GET_PEOPLE_REQUESTS_REQUEST:
        case API_GET_APPROVED_PEOPLE_REQUEST:
            return {
                ...state,
                loading: true
            }
        case API_GET_APPROVED_PEOPLE_SUCCESS:
            return {
                ...state,
                loading: false,
                approved: action.people,
            }
        case API_GET_PEOPLE_REQUESTS_SUCCESS:
            return {
                ...state,
                loading: false,
                requests: {
                    count: action.count,
                    items: action.requests
                }
            }
        case VIEW_USER_DETAILS:
            return {
                ...state,
                selected: action.user
            }
        default:
            return state
    }
}

// Action Creators
export function getPeople(organisation, filters, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_APPROVED_PEOPLE_REQUEST})

        try {
            const response = await axios.get(`${API_URL}/project/${organisation.uuid}/members${getQueryParams('?', filters)}`, getAuthorizationHeader())
            const pagination = paginationFromCount(response.data.total)

            const {entities: {people}} = normalize(response.data.data, [peopleSchema])
            dispatch({type: API_GET_APPROVED_PEOPLE_SUCCESS, people: people || {}})

            if (callback) {
                dispatch(() => callback(pagination))
            }
        }
        catch (error) {
            const errorCode = idx(error, _ => _.data.status) || ''
            errorDispatcher(errorCode, API_GET_APPROVED_PEOPLE_ERROR, dispatch)
        }
    }
}

export function getGroupPeople(organisation, group, filters, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_APPROVED_PEOPLE_REQUEST})
        try {
            const response = await axios.get(`${API_URL}/group/${group.uuid}/members${getQueryParams('?', filters)}`, getAuthorizationHeader())
            const {entities: {people}} = normalize(response.data.data, [peopleSchema])
            const pagination = paginationFromCount(response.data.total)
            dispatch({type: API_GET_APPROVED_PEOPLE_SUCCESS, people: people || {}})

            if (callback) {
                dispatch(() => callback(pagination))
            }
        }
        catch (error) {
            console.log("getGroupPeople", error)
            const errorCode = idx(error, _ => _.response.status) || ''
            errorDispatcher(errorCode, API_GET_APPROVED_PEOPLE_ERROR, dispatch)
        }
    }
}

export function deleteUserFromGroup(organisation, group, user, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_DELETE_PEOPLE_REQUEST})

        const groupId = group.groupUuid || group.uuid

        try {
            await axios.delete(`${API_URL}/group/${groupId}/member/${user.id}`, getAuthorizationHeader())
            dispatch({type: API_DELETE_PEOPLE_SUCCESS})

            if (callback) {
                dispatch(callback)
            }
        }
        catch (error) {
            console.error("deleteUserFromGroup", error)
            const errorCode = idx(error, _ => _.response.status) || ''
            errorDispatcher(errorCode, API_DELETE_PEOPLE_ERROR, dispatch)
        }
    }
}

export function getPeopleRequests(organisation, filters, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_PEOPLE_REQUESTS_REQUEST})

        try {
            const response = await axios.get(`${API_URL}/project/${organisation.uuid}/requests`, getAuthorizationHeader())
            if (!response.data) {
                throw Error
            }
            const {entities: {requests}} = normalize(response.data, [requestSchema])
            dispatch({type: API_GET_PEOPLE_REQUESTS_SUCCESS, requests: requests || {}, count: response.data.length})

            if (callback) {
                dispatch(() => callback(paginationFromCount(response.data.length)))
            }
        }
        catch (error) {
            console.error("getPeopleRequests", error)
            const errorCode = idx(error, _ => _.response.status) || ''
            errorDispatcher(errorCode, API_GET_PEOPLE_REQUESTS_ERROR, dispatch)
        }
    }
}

export function deletePeople(organisation, userIds, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_DELETE_PEOPLE_REQUEST})

        try {
            await axios.delete(
                `${API_URL}/project/${organisation.uuid}/members`,
                {
                    data: {userIds},
                    ...getAuthorizationHeader(),
                }
            ).then(() => {
                dispatch({type: API_DELETE_PEOPLE_SUCCESS})

                if (callback) {
                    dispatch(callback)
                }
            }).catch((error) => {
                return Promise.reject(error.response)
            })
        }
        catch (error) {
            const errorCode = idx(error, _ => _.data.status) || ''
            errorDispatcher(errorCode, API_DELETE_PEOPLE_ERROR, dispatch)
        }
    }
}

export function acceptRequests(organisation, requests, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_ACCEPT_REQUESTS_REQUEST})
        try {
            await axios.post(`${API_URL}/project/joinrequests/approve`, requests.map(r => r.uuid), getAuthorizationHeader())
            dispatch({type: API_ACCEPT_REQUESTS_SUCCESS, requests})
            dispatch(callback)
        }
        catch (error) {
            const errorCode = idx(error, _ => _.data.status) || ''
            errorDispatcher(errorCode, API_ACCEPT_REQUESTS_ERROR, dispatch)
        }
    }
}

export function denyRequests(organisation, requests, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_DENY_REQUESTS_REQUEST})
        try {
            await axios.delete(
                `${API_URL}/project/joinrequests`,
                {
                    data: requests.map(r => r.uuid),
                    ...getAuthorizationHeader(),
                }
            )
            dispatch({type: API_DENY_REQUESTS_SUCCESS, requests})
            dispatch(callback)
        }
        catch (error) {
            const errorCode = idx(error, _ => _.data.status) || ''
            errorDispatcher(errorCode, API_DENY_REQUESTS_ERROR, dispatch)
        }
    }
}

export function assignPeopleToGroups(organisation, people, groupUuids, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_ASSIGN_GROUPS_REQUEST})
        try {
            const userUuids = people.length ? people : [people.id]
            await axios.put(`${API_URL}/groups/members`, {
                accountIds: userUuids,
                groupIds: groupUuids
            }, getAuthorizationHeader()).then(() => {
                dispatch({type: API_ASSIGN_GROUPS_SUCCESS, userUuids})
                if (callback) {
                    dispatch(callback)
                }
            })
                .catch((error) => {
                    return Promise.reject(error.response)
                })
        }
        catch (error) {
            const errorCode = idx(error, _ => _.data.status) || ''
            errorDispatcher(errorCode, API_ASSIGN_GROUPS_ERROR, dispatch)
        }
    }
}

export function viewUserDetails(user) {
    return async function dispatcher(dispatch) {
        dispatch({type: VIEW_USER_DETAILS, user})
        dispatch(push('/dashboard/people/details'))
    }
}



// WEBPACK FOOTER //
// ./src/modules/people.js